import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaTruck, FaUndo, FaStar, FaRegStar } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import OrderTimeline from "./OrderTimeline";
import {
  fetchUserOrders,
  returnBook,
  cancelUserOrder,
  cancelUserReturnRequest,
} from "../../../features/orderSlice";
import config from "../../../config";
import { useState } from "react";
import { addReview } from "../../../features/bookReviewSlice";
import ReviewModal from "./ReviewModal";
import { FiPackage, FiRefreshCw } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import EmptyOrders from "./EmptyOrder";
import toast from "react-hot-toast";

const statusColors = {
  PENDING:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300",
  BOOK_ORDERED:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300",
  COMPLETED:
    "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300",
  CANCELLED: "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300",
  ACTIVE:
    "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300",
};

const deliveryStatusIcons = {
  PROCESSING: FiPackage,
  SHIPPED: FaTruck,
  ON_THE_WAY: FaTruck,
  BOOK_WITH_ME: FiPackage,
  RETURN_REQUESTED: FiRefreshCw,
  PICKUP_SCHEDULED: FiPackage,
  RETURN_IN_TRANSIT: FaTruck,
  RETURNED: FiPackage,
  CANCELLED: FiPackage,
};

const formatFromArray = (dateArr) => {
  if (
    !Array.isArray(dateArr) ||
    dateArr.length < 6 ||
    typeof dateArr[0] !== "number"
  ) {
    return "N/A";
  }

  // Destructure
  const [year, month, day, hour, minute, second, nanosecond = 0] = dateArr;

  // JavaScript Date constructor expects monthIndex = month - 1,
  // milliseconds as the last argument.
  const milli = Math.floor(nanosecond / 1_000_000);
  const dt = new Date(year, month - 1, day, hour, minute, second, milli);

  return dt.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const MyOrders = () => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth.user?.userId);
  const { userOrders, status } = useSelector((state) => state.orders);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserOrders(userId));
    }
  }, [userId, dispatch]);

  const handleReturnRequest = (orderId) => {
    const collectedBy = "User";
    dispatch(returnBook({ orderId, collectedBy }))
      .unwrap()
      .then(() => {
        dispatch(fetchUserOrders(userId)); // Refresh orders after return
      })
      .catch((err) => {
        console.error("Return request failed", err);
        alert("Return request failed. Please try again.");
      });
  };

  if (status === "loading") {
    return (
      <div className="p-6 bg-primary-shade1 min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-500">Loading orders...</p>
      </div>
    );
  }

  if (status === "succeeded" && (!userOrders || userOrders.length === 0)) {
    return <EmptyOrders />;
  }

  const getTimelineStatus = (order) => {
    if (
      order.returnStatus === "REQUESTED" ||
      ["PICKUP_SCHEDULED", "RETURN_IN_TRANSIT", "RETURNED"].includes(
        order.deliveryStatus
      )
    ) {
      return order.deliveryStatus;
    }
    return order.deliveryStatus;
  };

  const openReviewModal = (book) => {
    setSelectedBook(book);
    setShowReviewModal(true);
  };

  const handleSubmitReview = () => {
    if (!rating || !reviewText.trim()) {
      alert("Please provide a rating and a review text.");
      return;
    }

    dispatch(
      addReview({
        userId,
        bookId: selectedBook?.bookId,
        rating,
        review: reviewText, // 👈 Notice: pass `review`, not `reviewText`
      })
    )
      .unwrap()
      .then(() => {
        alert("Review submitted successfully!");
        setShowReviewModal(false);
        setRating(0);
        setReviewText("");
        // Optional: dispatch(fetchReviewsByBook(selectedBook.bookId)); if you want to refresh review list
      })
      .catch((error) => {
        console.error("Error submitting review:", error);
        alert("Something went wrong. Please try again.");
      });
  };

  const handleCancelOrder = (orderId) => {
    if (!userId || !orderId) {
      console.error("Invalid userId or orderId for cancellation.");
      return;
    }

    dispatch(cancelUserOrder({ orderId, userId }))
      .unwrap()
      .then(() => {
        toast.success("Order cancelled successfully!");
        dispatch(fetchUserOrders(userId)); // Refresh after cancellation
      })
      .catch((err) => {
        console.error("Cancel order failed:", err);
        toast.error("Failed to cancel order. Please try again.");
      });
  };

  const handleCancelReturnRequest = (orderId) => {
    if (!userId || !orderId) {
      console.error(
        "Invalid userId or orderId for return request cancellation."
      );
      return;
    }

    dispatch(cancelUserReturnRequest({ orderId, userId }))
      .unwrap()
      .then(() => {
        toast.success("Return request cancelled successfully!");
        dispatch(fetchUserOrders(userId)); // Refresh orders
      })
      .catch((err) => {
        console.error("Cancel return request failed:", err);
        alert("Failed to cancel return request. Please try again.");
      });
  };
  return (
    <div className="p-4 bg-primary-shade1 dark:bg-slate-900 min-h-screen">
      <motion.h1
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="text-3xl font-bold bg-gradient-to-r from-primary-shade4 to-primary-shade2 bg-clip-text text-transparent mb-8 text-center"
      >
        My Orders
      </motion.h1>

      <AnimatePresence>
        {status === "loading" ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-shade3 border-t-transparent" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-7xl mx-auto">
            <AnimatePresence>
              {userOrders
                .filter((order) => order !== null)
                .reverse()
                .map((order, index) => {
                  const book = order.book ?? {};
                  const author = book?.author ?? {};
                  const DeliveryIcon =
                    deliveryStatusIcons[order.deliveryStatus] || FaTruck;

                  return (
                    <motion.div
                      key={order.orderId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: index * 0.05 }}
                      className="group relative bg-white dark:bg-slate-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 p-3"
                    >
                      <div className="flex gap-4">
                        {/* Book Image */}
                        {book?.bookImages?.length > 0 && (
                          <div
                            className="w-28 flex-shrink-0 cursor-pointer"
                            onClick={() => navigate(`/book/${book.bookId}`)}
                          >
                            <img
                              src={`${config.url}${book.bookImages[0]}`}
                              alt={book.bookName}
                              className="w-full h-40 object-contain bg-white p-2 rounded"
                            />
                          </div>
                        )}

                        <div className="flex flex-col justify-between flex-1 space-y-1">
                          {/* Title + Status */}
                          <div className="flex justify-between items-start">
                            <div>
                              <h2 className="text-base font-semibold text-slate-800 dark:text-white">
                                {book?.bookName || "Unknown Book"}
                              </h2>

                              {/* Author Name - larger font */}
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                by {author?.authorName || "Unknown Author"}
                              </p>

                              {/* Date - larger font */}
                              <p className="text-xs text-slate-400 mt-0.5">
                                {formatFromArray(
                                  order.statusTimestamps?.[order.deliveryStatus]
                                )}
                              </p>
                            </div>

                            {/* Status Badge */}
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${
                                statusColors[order.orderStatus]
                              }`}
                            >
                              {order.orderStatus}
                            </span>
                          </div>

                          {/* Delivery status */}
                          <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                            <DeliveryIcon className="w-4 h-4 text-primary-shade4" />
                            <span>
                              {order.deliveryStatus.replace(/_/g, " ")}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Timeline and Action Buttons */}
                      <div className="mt-1">
                        <OrderTimeline
                          currentStatus={getTimelineStatus(order)}
                        />

                        <div className="flex flex-wrap gap-2 text-xs">
                          {["PROCESSING", "SHIPPED", "ON_THE_WAY"].includes(
                            order.deliveryStatus
                          ) && (
                            <button
                              onClick={() => handleCancelOrder(order.orderId)}
                              className="bg-red-600 text-white rounded px-3 py-1 hover:bg-red-700"
                            >
                              Cancel Order
                            </button>
                          )}

                          {order.deliveryStatus === "RETURN_REQUESTED" && (
                            <button
                              onClick={() =>
                                handleCancelReturnRequest(order.orderId)
                              }
                              className="bg-yellow-600 text-white rounded px-3 py-1 hover:bg-yellow-700"
                            >
                              Cancel Return Request
                            </button>
                          )}

                          {order.orderStatus === "ACTIVE" &&
                            order.returnStatus === "NOT_RETURNED" && (
                              <>
                                <button
                                  onClick={() =>
                                    handleReturnRequest(order.orderId)
                                  }
                                  className="bg-blue-600 text-white rounded px-3 py-1 hover:bg-blue-700"
                                >
                                  Request Return
                                </button>
                                {order.deliveryStatus === "BOOK_WITH_ME" && (
                                  <button
                                    onClick={() => openReviewModal(book)}
                                    className="bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-white rounded px-3 py-1 hover:bg-slate-300 dark:hover:bg-slate-600"
                                  >
                                    Write Review
                                  </button>
                                )}
                              </>
                            )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
            </AnimatePresence>
          </div>
        )}
      </AnimatePresence>

      {/* Review Modal */}
      <ReviewModal
        show={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        selectedBook={selectedBook}
        rating={rating}
        setRating={setRating}
        reviewText={reviewText}
        setReviewText={setReviewText}
        handleSubmitReview={handleSubmitReview}
      />

      {/* Keep empty state and modal the same */}
    </div>
  );
};

export default MyOrders;
